import {
  format as formatDateTime,
  isPast,
  formatRelative,
  parse,
  isToday,
  isTomorrow,
  isYesterday,
  isBefore,
  isAfter,
  isSameDay,
  parseISO
} from 'date-fns'
import { UTCDate } from '@date-fns/utc'

// see docs at https://date-fns.org/

export const formatDate = (date, format = 'EEE d MMM yyyy') => {
  if (!date || date === 'Invalid Date') return ''
  return formatDateTime(date, format)
}

export const formatTime = (time, format = 'h:mm a') => {
  if (!time || time === 'Invalid Time') return ''

  let date = new Date()
  let formattedTime = time

  if (time.includes('T')) {
    formattedTime = time.substring(time.indexOf('T') + 1)
  }

  if (formattedTime.includes(':')) {
    const [hours, minutes] = formattedTime.split(':')
    date.setHours(Number(hours))
    date.setMinutes(Number(minutes))
    date.setSeconds(0)
  }

  return formatDateTime(date, format) || ''
}

export const minutesToTimePickerValue = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  const label = `${String(hours > 12 ? hours - 12 : hours)}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`
  return { value, label }
}

export const getMinutesFrom24hTime = (time) => {
  if (!time) return 0
  const [hours, minutes] = time.split(':')
  return Number(hours) * 60 + Number(minutes)
}

export const parseDate = (date, format = 'yyyyMMdd', referenceDate = new Date(1, 1, 1970)) => {
  if (!date || date === 'Invalid Date') return ''

  let parsedDate
  if (format === 'ISO)') parsedDate = parseISO(date)
  else parsedDate = parse(date, format, referenceDate)

  return parsedDate
}

export const dateIsInThePast = (date, format) => {
  if (!date || date === 'Invalid Date') return ''

  if (format) date = parseDate(date, format)
  return isPast(date) && !isToday(date)
}

export const dateIsBefore = (date1, date2) => {
  if (!date1 || !date2 === 'Invalid Dates') return false

  return isBefore(date1, date2)
}

export const dateIsAfter = (date1, date2) => {
  if (!date1 || !date2 === 'Invalid Dates') return false

  return isAfter(date1, date2)
}

export const dateIsSame = (date1, date2) => {
  if (!date1 || !date2 === 'Invalid Dates') return false

  return isSameDay(date1, date2)
}

export const dateIsToday = (date, format) => {
  if (!date || date === 'Invalid Date') return ''

  if (format) date = parseDate(date, format)
  return isToday(date)
}

export const dateIsTomorrow = (date, format) => {
  if (!date || date === 'Invalid Date') return ''

  if (format) date = parseDate(date, format)
  return isTomorrow(date)
}

export const dateIsYesterday = (date, format) => {
  if (!date || date === 'Invalid Date') return ''

  if (format) date = parseDate(date, format)
  return isYesterday(date)
}

export const getRelativeDate = (date, format) => {
  if (!date || date === 'Invalid Date') return ''

  if (format) date = parseDate(date, format)
  return formatRelative(date, new Date())
}

export const isExpired = (date, marginSeconds = 10) => {
  const dateToTest = new Date(date)

  // take a margin off the expiry date to ensure we don't cut it too close
  const shortenedDateToTest = dateToTest.setSeconds(dateToTest.getSeconds() - marginSeconds)

  // is the shortened date in the past?
  const now = new Date()
  return shortenedDateToTest < now
}

export const toEndOfDayIsoString = ({ date = new Date() }) => {
  date.setHours(23, 59, 59, 999)
  return date.toISOString()
}

export const formatSecondsToHours = (seconds) => {
  if (!seconds) return '0:00'

  const date = new Date(0)
  date.setSeconds(seconds)
  return formatDate(date, 'H:mm')
}

export const getUtcDate = (date) => {
  if (!date) return new UTCDate()
  return new UTCDate(date)
}

export const convertDateToLocalTimeInIsoFormat = (date) => {
  if (!date) date = new Date()
  if (typeof date === 'string') date = new Date(date)

  // Get the local time components
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0')

  // Return ISO-like format but with local time values
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`
}
